import { Injectable } from '@angular/core';
import type { RequestHandler } from 'src/app/service/OffService/request-handler';
import { FormCommonApiService } from 'src/app/service/api/formcommon-api.service';
import { AnalisisApiService } from 'src/app/service/api/analisis-api.service';
import { FincasApiService } from 'src/app/service/api/fincas-api.service';
import { SiexApiService } from 'src/app/service/api/siex-api.service';
import { VisitasVariedadMesApiService } from 'src/app/service/api/visitas_variedad_mes-api.service';
import { UsuariosApiService } from 'src/app/service/api/usuarios-api.service';
import { TrabajadoresApiService } from 'src/app/service/api/trabajadores-api.service';
import type { ReactiveDropdownDataSource, ReactiveDropdownDataSourceContext, Value } from '@fitogest/reactive-form-builder/interfaces';
import {
    ReactiveCatalogSource,
    type ReactiveCatalogCompositeLoader,
    type ReactiveCatalogDependentLoader,
    type ReactiveParsedCatalogKey,
    type ReactiveSourceAwareCatalogRow
} from '@fitogest/reactive-form-builder/catalog';
import type { ComunidadesRegantesModel } from 'src/app/models/analisis/comunidadesRegantes.model';
import type { FincasModel } from 'src/app/models/form-common/fincas.module';
import type { ParcelasModel } from 'src/app/models/form-common/parcelas.model';
import type { SectorModel } from 'src/app/models/form-common/sector.model';
import type { VisitasVariedadMesModel } from 'src/app/models/visitas_variedad_mes/visitas_variedad_mes.model';
import type { ParcelasEnvironmentDefinition } from '../environment-variables';
import type { ParcelasDeclarativeDropdownDataSources } from './parcelas-declarative-dropdowns.config';

const DECLARATIVE_DROPDOWN_CACHE_TTL_MS = 600000;
const DECLARATIVE_DROPDOWN_TIMEOUT_MS = 30000;
export const PARCELAS_LOCAL_CATALOG_SOURCE = 'propio';
export const PARCELAS_SIEX_CATALOG_SOURCE = 'siex';
const LOCAL_CATALOG_SOURCE = PARCELAS_LOCAL_CATALOG_SOURCE;
const SIEX_CATALOG_SOURCE = PARCELAS_SIEX_CATALOG_SOURCE;
const CATALOG_SOURCES = [LOCAL_CATALOG_SOURCE, SIEX_CATALOG_SOURCE] as const;
const CATALOG_PLACEHOLDER_TOKENS = new Set([
    'cultivo',
    'id_cultivo',
    'codigo',
    'codigo_cultivo',
    'variedad',
    'id_variedad',
    'codigo_variedad',
]);

type CatalogSource = typeof CATALOG_SOURCES[number];
type VariedadCatalogLoadParams = {
    readonly codigoCultivo: string;
    readonly cultivo: string;
};
type ReactiveCatalogCompositeLoaderDefinition = ReactiveCatalogCompositeLoader<
    CatalogSource,
    VisitasVariedadMesModel,
    SourceAwareParcelasCatalogRow
>;
type ReactiveCatalogDependentLoaderDefinition = ReactiveCatalogDependentLoader<
    CatalogSource,
    VariedadCatalogLoadParams,
    VisitasVariedadMesModel,
    SourceAwareParcelasCatalogRow
>;

export type ParcelasCatalogSource = CatalogSource;
export type ParcelasParsedCatalogKey = ReactiveParsedCatalogKey<CatalogSource>;
export type SourceAwareParcelasCatalogRow = VisitasVariedadMesModel & ReactiveSourceAwareCatalogRow<CatalogSource>;

export type ParcelasDropdownDataSourceFactoryConfig = {
    readonly env: ParcelasEnvironmentDefinition;
    readonly addDefaultAplicador?: (row: Record<string, unknown>) => void;
};

/**
 * Builds Parcelas declarative dropdown data sources outside the component.
 *
 * The component still owns the form lifecycle, while endpoint selection, mapping, aliases and catalog
 * dependency details live here as a reusable adapter that can later feed a standalone ReactiveFormDefinition.
 */
@Injectable({ providedIn: 'root' })
export class ParcelasDropdownDataSourceFactory {

    public constructor(
        private readonly formApi: FormCommonApiService,
        private readonly analisisApi: AnalisisApiService,
        private readonly fincasApi: FincasApiService,
        private readonly siexApi: SiexApiService,
        private readonly visitasVariedadMesApi: VisitasVariedadMesApiService,
        private readonly usuariosApi: UsuariosApiService,
        private readonly trabajadoresApi: TrabajadoresApiService,
    ) { }

    public createDataSources(config: ParcelasDropdownDataSourceFactoryConfig): ParcelasDeclarativeDropdownDataSources {
        return {
            finca: () => this.createFincaDataSource(),
            sector: () => this.createSectorDataSource(),
            comunidadRegantes: () => this.createComunidadesRegantesDataSource(),
            origenAguaRiego: () => this.createOrigenAguaRiegoDataSource(),
            sistemaRiego: () => this.createSistemaRiegoDataSource(),
            aplicadores: () => this.createAplicadoresDataSource(config),
            cultivoCatalog: () => this.createCultivoCatalogDataSource(config),
            variedadCatalog: () => this.createVariedadCatalogDataSource(),
        };
    }

    public mapCultivoDropdownOption(item: unknown): Value<unknown> {
        const row = item as SourceAwareParcelasCatalogRow;

        return {
            label: this.toStringId(row.cultivo),
            value: row.catalogKey,
            raw: row,
            valueAliases: this.buildCultivoCatalogAliases(row),
        };
    }

    public mapVariedadDropdownOption(item: unknown): Value<unknown> {
        const row = item as SourceAwareParcelasCatalogRow;

        return {
            label: this.toStringId(row.variedad),
            value: row.catalogKey,
            raw: row,
            valueAliases: this.buildVariedadCatalogAliases(row),
        };
    }

    public toStringId(value: unknown): string {
        return ReactiveCatalogSource.toStringId(value);
    }

    public toRecord(value: unknown): Record<string, unknown> {
        return value !== null && typeof value === 'object' ? value as Record<string, unknown> : {};
    }

    public readRecordValue(value: unknown, key: string): unknown {
        return ReactiveCatalogSource.readRecordValue(value, key);
    }

    public parseCatalogKey(value: unknown): ParcelasParsedCatalogKey {
        return ReactiveCatalogSource.parseKey(value, CATALOG_SOURCES);
    }

    public buildCatalogKey(source: CatalogSource, ...parts: readonly unknown[]): string {
        return ReactiveCatalogSource.buildKey(source, ...parts);
    }

    public resolveFirstCatalogValue(...values: readonly unknown[]): string {
        return ReactiveCatalogSource.resolveFirstValue(...values);
    }

    public resolveUsableCatalogCode(...values: readonly unknown[]): string {
        for (const value of values) {
            const normalizedValue = this.toStringId(value);

            if (normalizedValue !== '' && !this.isCatalogPlaceholderToken(normalizedValue)) {
                return normalizedValue;
            }
        }

        return '';
    }

    public isCatalogPlaceholderToken(value: unknown): boolean {
        const normalizedValue = this.normalizeCatalogName(value);

        return CATALOG_PLACEHOLDER_TOKENS.has(normalizedValue.toLowerCase());
    }

    private createFincaDataSource(): ReactiveDropdownDataSource<ParcelasModel> {
        return {
            endpoint: this.fincasApi.fincasGET,
            cacheKey: 'parcelas-fincas',
            ttlMs: DECLARATIVE_DROPDOWN_CACHE_TTL_MS,
            timeoutMs: DECLARATIVE_DROPDOWN_TIMEOUT_MS,
            labelKeys: ['nombre'],
            valueKeys: ['id'],
            distinctBy: 'id',
            mapOption: (item) => this.mapFincaDropdownOption(item),
        };
    }

    private createSectorDataSource(): ReactiveDropdownDataSource<ParcelasModel> {
        return {
            endpoint: this.fincasApi.sectoresGET,
            cacheKey: 'parcelas-sectores',
            dependsOn: ['id_finca'],
            ttlMs: DECLARATIVE_DROPDOWN_CACHE_TTL_MS,
            timeoutMs: DECLARATIVE_DROPDOWN_TIMEOUT_MS,
            labelKeys: ['nombre'],
            valueKeys: ['id'],
            distinctBy: 'id',
            filterOption: (option, context) => this.doesSectorMatchSelectedFinca(option.raw ?? option.value, context),
            mapOption: (item) => this.mapSectorDropdownOption(item),
        };
    }

    private createComunidadesRegantesDataSource(): ReactiveDropdownDataSource<ParcelasModel> {
        return {
            endpoint: this.analisisApi.comunidadesRegantes.GET,
            cacheKey: 'parcelas-comunidades-regantes',
            ttlMs: DECLARATIVE_DROPDOWN_CACHE_TTL_MS,
            timeoutMs: DECLARATIVE_DROPDOWN_TIMEOUT_MS,
            labelKeys: ['nombre'],
            valueKeys: ['id'],
            distinctBy: 'id',
            mapOption: (item) => this.mapComunidadesRegantesDropdownOption(item),
        };
    }

    private createOrigenAguaRiegoDataSource(): ReactiveDropdownDataSource<ParcelasModel> {
        return {
            endpoint: this.siexApi.origenesAguaRiegoGET,
            cacheKey: 'parcelas-origenes-agua-riego',
            ttlMs: DECLARATIVE_DROPDOWN_CACHE_TTL_MS,
            timeoutMs: DECLARATIVE_DROPDOWN_TIMEOUT_MS,
            labelKeys: ['procedencia_del_agua_de_riego'],
            valueKeys: ['codigo_SIEX'],
            distinctBy: 'codigo_SIEX',
            mapOption: (item) => this.mapOrigenAguaRiegoDropdownOption(item),
        };
    }

    private createSistemaRiegoDataSource(): ReactiveDropdownDataSource<ParcelasModel> {
        return {
            endpoint: this.siexApi.sistemasRiegoGET,
            cacheKey: 'parcelas-sistemas-riego',
            dependsOn: ['codigo_secano_regadio'],
            ttlMs: DECLARATIVE_DROPDOWN_CACHE_TTL_MS,
            timeoutMs: DECLARATIVE_DROPDOWN_TIMEOUT_MS,
            labelKeys: ['sistema_de_riego'],
            valueKeys: ['codigo_SIEX'],
            distinctBy: 'codigo_SIEX',
            filterOption: (_option, context) => this.resolveContextFieldId(context, 'codigo_secano_regadio') === 'R',
            mapOption: (item) => this.mapSistemaRiegoDropdownOption(item),
        };
    }

    private createAplicadoresDataSource(config: ParcelasDropdownDataSourceFactoryConfig): ReactiveDropdownDataSource<ParcelasModel> {
        return {
            endpoint: config.env.appName === 'sequiador' ? this.usuariosApi.usuariosGET : this.trabajadoresApi.trabajadoresGET,
            cacheKey: `parcelas-aplicadores-${config.env.appName}`,
            ttlMs: DECLARATIVE_DROPDOWN_CACHE_TTL_MS,
            timeoutMs: DECLARATIVE_DROPDOWN_TIMEOUT_MS,
            labelKeys: ['nombre', 'apellidos'],
            valueKeys: ['id'],
            distinctBy: 'id',
            filterOption: (option) => this.shouldIncludeAplicadorOption(option.raw ?? option.value, config),
            mapOption: (item) => this.mapAplicadorDropdownOption(item),
        };
    }

    private createCultivoCatalogDataSource(config: ParcelasDropdownDataSourceFactoryConfig): ReactiveDropdownDataSource<ParcelasModel> {
        return {
            endpoint: () => this.loadDistinctCultivoCatalogRows(config),
            cacheKey: 'parcelas-cultivos-siex-composite',
            ttlMs: DECLARATIVE_DROPDOWN_CACHE_TTL_MS,
            timeoutMs: DECLARATIVE_DROPDOWN_TIMEOUT_MS,
            labelKeys: ['cultivo'],
            valueKeys: ['catalogKey'],
            distinctBy: (item) => this.resolveCultivoCatalogDistinctKey(item),
            mapResponse: (response) => Array.isArray(response) ? response : [],
            mapOption: (item) => this.mapCultivoDropdownOption(item),
        };
    }

    private createVariedadCatalogDataSource(): ReactiveDropdownDataSource<ParcelasModel> {
        return ReactiveCatalogSource.createDependentDropdownDataSource<
            ParcelasModel,
            CatalogSource,
            VariedadCatalogLoadParams,
            VisitasVariedadMesModel,
            SourceAwareParcelasCatalogRow
        >({
            cacheKey: 'parcelas-variedades-by-cultivo',
            dependsOn: ['id_cultivo'],
            params: (context: ReactiveDropdownDataSourceContext<ParcelasModel>) => this.buildVariedadesParams(context),
            resolveSource: (params) => this.resolveCatalogSource(params['source']),
            mapParams: (params) => this.mapVariedadCatalogParams(params),
            shouldLoad: (params) => params.codigoCultivo !== '' || params.cultivo !== '',
            sources: this.getVariedadCatalogSources(),
            mapOption: (item) => this.mapVariedadDropdownOption(item),
            options: {
                lazy: true,
                ttlMs: DECLARATIVE_DROPDOWN_CACHE_TTL_MS,
                timeoutMs: DECLARATIVE_DROPDOWN_TIMEOUT_MS,
                labelKeys: ['variedad'],
                valueKeys: ['catalogKey'],
                distinctBy: 'catalogKey',
            },
        });
    }

    private mapFincaDropdownOption(item: unknown): Value<unknown> {
        const row = this.toRecord(item) as Partial<FincasModel>;
        const id = this.toStringId(row['id']);

        return {
            label: this.toStringId(row['nombre'] ?? id),
            value: id,
            raw: item,
            valueAliases: this.buildPrimitiveAliases(row['id'], row['id_finca'], row['nombre']),
        };
    }

    private mapSectorDropdownOption(item: unknown): Value<unknown> {
        const row = this.toRecord(item) as Partial<SectorModel>;
        const id = this.toStringId(row['id']);

        return {
            label: this.toStringId(row['nombre'] ?? id),
            value: id,
            raw: item,
            valueAliases: this.buildPrimitiveAliases(row['id'], row['id_sector'], row['nombre']),
        };
    }

    private mapComunidadesRegantesDropdownOption(item: unknown): Value<unknown> {
        const row = this.toRecord(item) as Partial<ComunidadesRegantesModel>;
        const id = this.toStringId(row['id']);

        return {
            label: this.toStringId(row['nombre'] ?? id),
            value: id,
            raw: item,
            valueAliases: this.buildPrimitiveAliases(row['id'], row['nombre']),
        };
    }

    private mapOrigenAguaRiegoDropdownOption(item: unknown): Value<unknown> {
        const row = this.toRecord(item);
        const codigo = this.toStringId(row['codigo_SIEX']);

        return {
            label: this.toStringId(row['procedencia_del_agua_de_riego'] ?? codigo),
            value: codigo,
            raw: item,
            valueAliases: this.buildPrimitiveAliases(row['codigo_SIEX'], row['procedencia_del_agua_de_riego']),
        };
    }

    private mapSistemaRiegoDropdownOption(item: unknown): Value<unknown> {
        const row = this.toRecord(item);
        const codigo = this.toStringId(row['codigo_SIEX']);

        return {
            label: this.toStringId(row['sistema_de_riego'] ?? codigo),
            value: codigo,
            raw: item,
            valueAliases: this.buildPrimitiveAliases(row['codigo_SIEX'], row['sistema_de_riego']),
        };
    }

    private mapAplicadorDropdownOption(item: unknown): Value<unknown> {
        const row = this.toRecord(item);
        const id = this.toStringId(row['id']);

        return {
            label: this.formatPersonName(row, id),
            value: id,
            raw: item,
            valueAliases: this.buildPrimitiveAliases(row['id'], row['nombre'], row['apellidos']),
        };
    }

    private doesSectorMatchSelectedFinca(
        item: unknown,
        context: ReactiveDropdownDataSourceContext<ParcelasModel>,
    ): boolean {
        const sector = this.toRecord(item) as Partial<SectorModel>;
        const sectorFincaId = this.toStringId(sector['id_finca']);
        const selectedFincaId = this.resolveContextFieldId(context, 'id_finca', 'id', 'id_finca');

        return sectorFincaId !== '' && selectedFincaId !== '' && sectorFincaId === selectedFincaId;
    }

    private resolveContextFieldId(
        context: ReactiveDropdownDataSourceContext<ParcelasModel>,
        fieldName: string,
        ...rawKeys: readonly string[]
    ): string {
        const controlValue = context.model?.get(fieldName)?.value;
        const controlId = this.toStringId(controlValue);

        if (controlId !== '') {
            return controlId;
        }

        const selectedRaw = context.getSelectedRaw<Record<string, unknown>>(fieldName);

        for (const key of rawKeys) {
            const rawId = this.toStringId(selectedRaw?.[key]);

            if (rawId !== '') {
                return rawId;
            }
        }

        return '';
    }

    private shouldIncludeAplicadorOption(item: unknown, config: ParcelasDropdownDataSourceFactoryConfig): boolean {
        const row = this.toRecord(item);

        if (config.env.appName === 'sequiador') {
            const isResponsible = this.toStringId(row['rol']) === 'responsable';
            const hasPlatformAccess = this.toStringId(row['acceso_plataforma']) === '1';

            if (isResponsible && hasPlatformAccess) {
                config.addDefaultAplicador?.(row);
                return true;
            }
        }

        if (config.env.appName === 'avafitogest') {
            return this.toStringId(row['tipo_carnet']).includes('ASESOR');
        }

        return false;
    }

    private formatPersonName(row: Record<string, unknown>, fallback: string): string {
        const nombre = this.toStringId(row['nombre']);
        const apellidos = this.toStringId(row['apellidos']);
        const fullName = [nombre, apellidos].filter((value) => value !== '').join(' ');

        return fullName !== '' ? fullName : fallback;
    }

    private buildPrimitiveAliases(...values: readonly unknown[]): string[] {
        return Array.from(new Set(values
            .map((value) => this.toStringId(value))
            .filter((value) => value !== '')));
    }

    private buildVariedadesParams(context: ReactiveDropdownDataSourceContext<ParcelasModel>): Record<string, unknown> {
        const selectedCultivo = context.getSelectedRaw<SourceAwareParcelasCatalogRow>('id_cultivo');
        const controlValue = context.model?.get('id_cultivo')?.value;
        const parsedValue = this.parseCatalogKey(controlValue);
        const previousData = this.toRecord(context.previousData);
        const codigoCultivo = this.resolveCatalogCode(
            this.resolveCultivoCode(selectedCultivo, controlValue, parsedValue),
        );
        const cultivo = this.resolveCatalogLabel(
            selectedCultivo?.cultivo,
            context.getSelectedLabel('id_cultivo'),
            this.readRecordValue(controlValue, 'cultivo'),
            previousData['cultivo_nombre'],
            previousData['nombre_cultivo'],
            previousData['cultivo'],
        );
        const source = this.resolveCatalogSource(
            selectedCultivo?.source ??
            parsedValue.source ??
            this.readRecordValue(controlValue, 'source'),
        );
        const params: Record<string, unknown> = { source };

        if (codigoCultivo !== '' && !this.isSameCatalogValue(codigoCultivo, cultivo)) {
            params['codigo_cultivo'] = codigoCultivo;
        }

        if (cultivo !== '') {
            params['cultivo'] = cultivo;
        }

        return params;
    }

    private async loadDistinctCultivoCatalogRows(
        config: ParcelasDropdownDataSourceFactoryConfig,
    ): Promise<SourceAwareParcelasCatalogRow[]> {
        const rows = await ReactiveCatalogSource.loadComposite(this.getCultivoCatalogSources(config));

        return this.deduplicateCultivoCatalogRows(rows);
    }

    private getCultivoCatalogSources(config: ParcelasDropdownDataSourceFactoryConfig): readonly ReactiveCatalogCompositeLoaderDefinition[] {
        return [
            {
                source: LOCAL_CATALOG_SOURCE,
                enabled: config.env.hasCultivosPropios,
                load: () => this.readVisitasRequest(this.formApi.cultivosGET),
                map: (row, source) => this.withCultivoCatalogSource(row, source),
                label: (row) => row.cultivo,
            },
            {
                source: SIEX_CATALOG_SOURCE,
                load: () => this.readVisitasRequest(this.siexApi.cultivosGET),
                map: (row, source) => this.withCultivoCatalogSource(row, source),
                label: (row) => row.cultivo,
            },
        ];
    }

    private getVariedadCatalogSources(): readonly ReactiveCatalogDependentLoaderDefinition[] {
        return [
            {
                source: SIEX_CATALOG_SOURCE,
                load: (requestParams) => this.readVisitasRequest(
                    this.siexApi.variedadesPorCultivoGET,
                    this.buildVariedadRequestParams(requestParams),
                ),
                map: (row, catalogSource, requestParams) => this.withVariedadCatalogSource(row, catalogSource, requestParams),
                filter: (row, requestParams) => this.isVariedadForCultivo(row, requestParams.codigoCultivo, requestParams.cultivo),
                label: (row) => row.variedad,
            },
            {
                source: LOCAL_CATALOG_SOURCE,
                load: (requestParams) => this.readVisitasRequest(
                    this.visitasVariedadMesApi.variedadesPorCultivoGET,
                    this.buildVariedadRequestParams(requestParams),
                ),
                map: (row, catalogSource, requestParams) => this.withVariedadCatalogSource(row, catalogSource, requestParams),
                label: (row) => row.variedad,
            },
        ];
    }

    private mapVariedadCatalogParams(params: Record<string, unknown>): VariedadCatalogLoadParams {
        return {
            codigoCultivo: this.resolveCatalogCode(params['codigo_cultivo']),
            cultivo: this.resolveCatalogLabel(params['cultivo']),
        };
    }

    private buildVariedadRequestParams(params: VariedadCatalogLoadParams): Record<string, unknown> {
        const requestParams: Record<string, unknown> = {};
        const codigoCultivo = this.resolveCatalogCode(params.codigoCultivo);
        const cultivo = this.resolveCatalogLabel(params.cultivo);

        if (codigoCultivo !== '' && !this.isSameCatalogValue(codigoCultivo, cultivo)) {
            requestParams['codigo_cultivo'] = codigoCultivo;
        }

        if (cultivo !== '') {
            requestParams['cultivo'] = cultivo;
        }

        return requestParams;
    }

    private withCultivoCatalogSource(
        row: VisitasVariedadMesModel,
        source: CatalogSource,
    ): SourceAwareParcelasCatalogRow {
        const catalogCode = this.resolveCultivoCatalogCode(row);
        const catalogKey = this.buildCatalogKey(source, catalogCode);

        return {
            ...row,
            codigo: catalogCode,
            codigo_cultivo: catalogCode,
            source,
            catalogKey,
            catalogCode,
        };
    }

    private withVariedadCatalogSource(
        row: VisitasVariedadMesModel,
        source: CatalogSource,
        params: VariedadCatalogLoadParams,
    ): SourceAwareParcelasCatalogRow {
        const cultivoCode = this.resolveCatalogCode(
            row.codigo_cultivo,
            params.codigoCultivo,
            row.codigo,
            row.cultivo,
            params.cultivo,
        );
        const variedadCode = this.resolveCatalogCode(row.id, row.id_variedad, row.codigo_variedad, row.variedad)
            || this.resolveCatalogLabel(row.variedad);
        const catalogKey = this.buildCatalogKey(source, cultivoCode, variedadCode);

        return {
            ...row,
            source,
            catalogKey,
            catalogCode: variedadCode,
        };
    }

    private resolveCatalogSource(value: unknown): CatalogSource {
        return ReactiveCatalogSource.resolveSource(value, CATALOG_SOURCES, SIEX_CATALOG_SOURCE) ?? SIEX_CATALOG_SOURCE;
    }

    private resolveCultivoCode(
        selectedCultivo: SourceAwareParcelasCatalogRow | null,
        controlValue: unknown,
        parsedValue: ParcelasParsedCatalogKey = this.parseCatalogKey(controlValue),
    ): string {
        return this.resolveUsableCatalogCode(
            selectedCultivo?.catalogCode,
            selectedCultivo?.codigo,
            selectedCultivo?.codigo_cultivo,
            parsedValue.primaryCode,
            selectedCultivo?.id,
            this.readRecordValue(controlValue, 'codigo'),
            this.readRecordValue(controlValue, 'codigo_cultivo'),
            this.readRecordValue(controlValue, 'id'),
            controlValue,
        );
    }

    private resolveCultivoCatalogCode(row: VisitasVariedadMesModel): string {
        const hasVariedadContext = this.resolveCatalogLabel(row.variedad, row.id_variedad, row.codigo_variedad) !== '';
        const rowCodigo = hasVariedadContext ? undefined : row.codigo;
        const rowId = hasVariedadContext ? undefined : row.id;

        return this.resolveUsableCatalogCode(row.codigo_cultivo, row.id_cultivo, rowCodigo, rowId, row.cultivo);
    }

    private deduplicateCultivoCatalogRows(
        rows: readonly SourceAwareParcelasCatalogRow[],
    ): SourceAwareParcelasCatalogRow[] {
        const seen = new Set<string>();
        const deduplicatedRows: SourceAwareParcelasCatalogRow[] = [];

        for (const row of rows) {
            const distinctKey = this.resolveCultivoCatalogDistinctKey(row);

            if (distinctKey === '' || seen.has(distinctKey)) {
                continue;
            }

            seen.add(distinctKey);
            deduplicatedRows.push(row);
        }

        return deduplicatedRows;
    }

    private resolveCultivoCatalogDistinctKey(item: unknown): string {
        const row = this.toRecord(item);
        const cultivo = this.normalizeCatalogName(row['cultivo']);

        if (cultivo !== '') {
            return cultivo;
        }

        return this.toStringId(row['catalogKey'] ?? row['catalogCode'] ?? row['id'] ?? row['codigo']);
    }

    private buildCultivoCatalogAliases(row: SourceAwareParcelasCatalogRow): string[] {
        const values = [row.catalogKey, row.catalogCode, row.id, row.codigo, row.codigo_cultivo, row.id_cultivo, row.cultivo];

        return ReactiveCatalogSource.buildAliases({
            source: row.source,
            simpleValues: values,
            sourceValues: values,
        });
    }

    private buildVariedadCatalogAliases(row: SourceAwareParcelasCatalogRow): string[] {
        const parsedKey = this.parseCatalogKey(row.catalogKey);
        const cultivoValues = [parsedKey.primaryCode, row.codigo_cultivo, row.id_cultivo, row.codigo, row.cultivo];
        const variedadValues = [row.catalogKey, row.catalogCode, row.id, row.id_variedad, row.codigo_variedad, row.variedad];

        return ReactiveCatalogSource.buildAliases({
            source: row.source,
            simpleValues: variedadValues,
            primaryValues: cultivoValues,
            secondaryValues: variedadValues,
        });
    }

    private isVariedadForCultivo(
        row: VisitasVariedadMesModel,
        codigoCultivo: string,
        cultivo: string,
    ): boolean {
        const rowCodigoCultivo = this.toStringId(row.codigo_cultivo ?? row.codigo);
        const rowCultivo = this.normalizeCatalogName(row.cultivo);

        if (rowCodigoCultivo === '' && rowCultivo === '') {
            return true;
        }

        if (codigoCultivo !== '' && rowCodigoCultivo !== '' && rowCodigoCultivo === codigoCultivo) {
            return true;
        }

        return cultivo !== '' && rowCultivo !== '' && rowCultivo === this.normalizeCatalogName(cultivo);
    }


    private isSameCatalogValue(left: string, right: string): boolean {
        return left !== '' && right !== '' && this.normalizeCatalogName(left) === this.normalizeCatalogName(right);
    }

    private resolveCatalogCode(...values: readonly unknown[]): string {
        return this.resolveUsableCatalogCode(...values);
    }

    private resolveCatalogLabel(...values: readonly unknown[]): string {
        for (const value of values) {
            const label = this.toStringId(value);

            if (label !== '' && !this.isCatalogPlaceholderToken(label)) {
                return label;
            }
        }

        return '';
    }

    private normalizeCatalogName(value: unknown): string {
        return ReactiveCatalogSource.normalizeName(value);
    }

    private readVisitasRequest(
        request: RequestHandler<VisitasVariedadMesModel[]>,
        params?: Record<string, unknown>,
    ): Promise<VisitasVariedadMesModel[]> {
        if (!params) {
            return request.toPromise().then((response) => Array.isArray(response) ? response : []);
        }

        const requestData = request.getRequestData();
        const requestParams = params as unknown as VisitasVariedadMesModel[];
        const requestPromise = request.toPromise(requestParams);

        // Restore the shared request handler defaults after capturing this request params.
        request.fetch(requestData.type, requestData.endpoint, requestData.params);

        return requestPromise.then((response) => Array.isArray(response) ? response : []);
    }
}
